import React from 'react';
import {Link} from '@mui/material';
import WikidataEntityDialog from '../../../components/prosemirror-editor/components/WikidataEntityDialog';
import MentionHoverCard from '../../../components/entityMentions/MentionHoverCard';
import {generateUserColor} from '../../../components/prosemirror-editor/helpers/utils/users';
import {getEntityKBUrl, isLinkedEntityRef} from '../../../utils/entityLinks';
import {resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import {shouldDisplayUnlinkedMentions, useEntityDisplayConfig} from '../../../hooks/entityDisplayConfig';
import {
    activatePreviewEntityDialog,
    deactivatePreviewEntityDialog,
} from './previewEntityDialogCoordinator';

function getMentionPillStyle(hovered) {
    return {
        fontWeight: 500,
        color: '#3d3d3d',
        backgroundColor: hovered ? '#d0d0d0' : '#e7e7e7',
        padding: '3px 8px 4px',
        borderRadius: '12px',
        display: 'inline-block',
        lineHeight: 1,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 0.2s ease',
    };
}

function getTagPillStyle(hovered) {
    return {
        fontWeight: 500,
        color: 'var(--mui-palette-secondary-main, #9c27b0)',
        backgroundColor: hovered ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
        padding: '3px 8px 4px',
        borderRadius: '12px',
        display: 'inline-block',
        lineHeight: 1,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 0.2s ease',
    };
}

/**
 * Returns a className string for a wikidataEntity or wikidataMention mark.
 *
 * Aligns with the CSS definitions in src/styles/global.css and
 * src/components/prosemirror-editor/styles/editor.css:
 *   --linked   → blue dashed underline, no background (all linked entities)
 *   --current  → blue background + dotted underline (the entity currently viewed on the KB page)
 *   --mention  → yellow underline (NER-recognised but not KB-linked)
 *
 * This replaces the previous inline-style approach so that hover states, transitions,
 * and visual weight are identical to the corpus viewer and search-results views.
 */
function getEntityMentionClassName(isMatch, isLinked = true) {
    if (!isLinked) {
        return isMatch
            ? 'wikidata-entity wikidata-entity--mention wikidata-entity--current'
            : 'wikidata-entity wikidata-entity--mention';
    }
    return isMatch
        ? 'wikidata-entity wikidata-entity--linked wikidata-entity--current'
        : 'wikidata-entity wikidata-entity--linked';
}

// Wrapper for wikidataEntity mark that links to internal KB and only highlights the current entity.
// On hover it shows the shared MentionHoverCard (name + predicted type + confidence), matching the
// data-items and corpus viewers so mention metadata is consistent everywhere.
const WikidataEntityMarkWrapper = ({
    id,
    uid = null,
    label,
    url,
    description,
    imageUrl,
    entityType,
    confidence,
    nerScore,
    linkingScore,
    children,
    isMatch,
    onWikidataEntityClick,
}) => {
    const {unlinkedUnderlineEnabled} = useEntityDisplayConfig();
    const [anchorEl, setAnchorEl] = React.useState(null);
    // PERF: Do NOT store the DOM element in React state — ref={setMentionEl} was calling setState
    // on every entity span mount, causing a render storm on large NER-annotated documents.
    const mentionRef = React.useRef(null);
    const isLinked = isLinkedEntityRef(id);
    const kbUrl = isLinked ? getEntityKBUrl(id) : null;
    const dialogSessionKey = uid || id || label;
    const hideUnlinkedMention = !isLinked && !shouldDisplayUnlinkedMentions(unlinkedUnderlineEnabled);

    React.useEffect(() => () => {
        if (anchorEl) {
            deactivatePreviewEntityDialog(dialogSessionKey);
        }
    }, [anchorEl, dialogSessionKey]);

    const handleClose = React.useCallback(() => {
        deactivatePreviewEntityDialog(dialogSessionKey);
        setAnchorEl(null);
    }, [dialogSessionKey]);

    const openLocalEntityDialog = React.useCallback((target) => {
        activatePreviewEntityDialog(dialogSessionKey, () => setAnchorEl(null));
        setAnchorEl(target);
    }, [dialogSessionKey]);

    const handleMouseDown = (event) => {
        if (!isLinked || onWikidataEntityClick) return;
        if (event.button !== 0) return;
        // Open on press (not click) so a visible hover tooltip cannot swallow the first click.
        event.preventDefault();
        openLocalEntityDialog(event.currentTarget);
    };

    const handleClick = (event) => {
        // Unlinked mentions are not clickable — they carry no KB/Wikidata target.
        if (!isLinked) return;
        event.preventDefault();
        event.stopPropagation();

        if (onWikidataEntityClick) {
            onWikidataEntityClick({
                type: 'wikidata',
                trigger: 'click',
                id,
                uid: uid || null,
                label,
                url: url || kbUrl,
                description: description || null,
                imageUrl: resolveEntityAvatarUrl({wikidataId: id, imageUrl: imageUrl || null}),
                anchorEl: event.currentTarget,
                anchorPosition: null,
                position: null,
                view: null,
            });
            return;
        }

        if (!anchorEl) {
            openLocalEntityDialog(event.currentTarget);
        }
    };

    if (hideUnlinkedMention) {
        return <>{children}</>;
    }

    return (
        <>
            <MentionHoverCard
                name={label}
                entityType={entityType}
                entityRef={isLinked ? id : null}
                confidence={confidence}
                nerScore={nerScore}
                linkingScore={linkingScore}
                mentionAnchorRef={mentionRef}
                mentionUid={uid}
                suppressHover={Boolean(anchorEl)}
            >
                <span
                    ref={mentionRef}
                    onMouseDown={handleMouseDown}
                    onClick={handleClick}
                    className={getEntityMentionClassName(isMatch, isLinked)}
                    style={{cursor: isLinked ? 'pointer' : 'default'}}
                    data-id={isLinked ? id : null}
                    data-wikidata-id={isLinked ? id : null}
                    data-wikidata-uid={uid || undefined}
                    data-url={kbUrl}
                >
                    {children}
                </span>
            </MentionHoverCard>
            {anchorEl && (
                <WikidataEntityDialog
                    open={true}
                    anchorEl={anchorEl}
                    id={id}
                    label={label}
                    url={url || kbUrl}
                    description={description}
                    entityType={entityType}
                    imageUrl={imageUrl}
                    onClose={handleClose}
                />
            )}
        </>
    );
};

const InlineRenderer = ({
                            node,
                            nodeKey,
                            referencesByMarkId,
                            referenceNumbers,
                            getReferenceNumber,
                            selectedAuthorId,
                            onUserMentionClick,
                            onHashtagClick,
                            onWikidataEntityClick,
                            currentEntityWikidataId,
                        }) => {
    if (!node) return null;

    // Suggestion-mode marks (@handlewithcare/prosemirror-suggest-changes): a pending
    // "insertion" is not yet part of the accepted document, so hide it from the read-only
    // preview entirely. A pending "deletion" is still part of the document until accepted,
    // so it renders normally below (no special handling needed). A pending "modification"
    // changed an attribute on this node; revert it to its pre-suggestion value so the preview
    // reflects the currently-accepted state rather than the proposed one.
    const nodeMarks = Array.isArray(node.marks) ? node.marks : [];
    if (nodeMarks.some((m) => m.type === 'insertion')) return null;
    const modificationMarks = nodeMarks.filter((m) => m.type === 'modification');
    if (modificationMarks.length && node.attrs) {
        const revertedAttrs = {...node.attrs};
        modificationMarks.forEach((m) => {
            const attrName = m.attrs?.attrName;
            if (attrName) revertedAttrs[attrName] = m.attrs?.previousValue;
        });
        node = {...node, attrs: revertedAttrs};
    }

    // Handle userMention inline node with click event
    if (node.type === 'userMention') {
        const {id, label, name, email, position, headshot} = node.attrs || {};

        const handleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (onUserMentionClick) {
                onUserMentionClick({
                    id,
                    label,
                    name,
                    email,
                    position,
                    headshot,
                    anchorEl: event.currentTarget
                });
            }
        };

        return (
            <span
                key={nodeKey}
                onClick={handleClick}
                style={getMentionPillStyle(false)}
                onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, getMentionPillStyle(true));
                }}
                onMouseLeave={(e) => {
                    Object.assign(e.currentTarget.style, getMentionPillStyle(false));
                }}
                data-mention-id={id}
                data-mention-label={label}
                data-mention-name={name}
                title={name ? `${name}${email ? ` (${email})` : ''}${position ? ` - ${position}` : ''}` : label}
            >
                {label || name || `@${id}`}
            </span>
        );
    }

    // Handle hashtag inline node with click event
    if (node.type === 'hashtag') {
        const {id, label, docId} = node.attrs || {};

        const handleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (onHashtagClick) {
                onHashtagClick({
                    id,
                    label,
                    docId,
                    anchorEl: event.currentTarget
                });
            }
        };

        return (
            <span
                key={nodeKey}
                onClick={handleClick}
                style={getTagPillStyle(false)}
                onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, getTagPillStyle(true));
                }}
                onMouseLeave={(e) => {
                    Object.assign(e.currentTarget.style, getTagPillStyle(false));
                }}
                data-tag-id={id}
                data-tag-label={label}
                data-tag-doc-id={docId}
                title={`Tag: ${label || id}`}
            >
                {label || `#${id}`}
            </span>
        );
    }

    // Render inline wikidata mentions from attrs (label + optional id/url)
    if (node.type === 'wikidataMention') {
        const label = node.attrs?.label ?? '';
        const id = node.attrs?.id;
        const url = node.attrs?.url || null;
        const description = node.attrs?.description || null;
        const imageUrl = resolveEntityAvatarUrl({
            wikidataId: id,
            imageUrl: node.attrs?.imageUrl || null,
        });
        if (!label) return null;

        const isMatch = !!(currentEntityWikidataId && id && id === currentEntityWikidataId);

        return (
            <WikidataEntityMarkWrapper
                key={nodeKey}
                id={id}
                uid={node.attrs?.uid || null}
                label={label}
                url={url}
                description={description}
                imageUrl={imageUrl}
                isMatch={isMatch}
                onWikidataEntityClick={onWikidataEntityClick}
            >
                {label}
            </WikidataEntityMarkWrapper>
        );
    }

    if (node.type === 'text') {
        const marks = node.marks || [];
        let wrapped = node.text ?? '';

        // Apply text styling marks from schema.js (and keep legacy aliases for older payloads).
        marks.forEach((mark) => {
            if (mark.type === 'strong' || mark.type === 'bold') {
                wrapped = <strong key={`strong-${nodeKey}`}>{wrapped}</strong>;
            }
            if (mark.type === 'em' || mark.type === 'italic') {
                wrapped = <em key={`em-${nodeKey}`}>{wrapped}</em>;
            }
            if (mark.type === 'code') {
                wrapped = <code key={`code-${nodeKey}`}>{wrapped}</code>;
            }
        });

        // Handle author mark for highlighting
        const authorMark = marks.find(m => m.type === 'author');
        let shouldHighlight = false;
        let authorColor = null;

        if (authorMark && selectedAuthorId) {
            const authorId = authorMark.attrs?.userId;
            if (authorId === selectedAuthorId) {
                shouldHighlight = true;
                authorColor = generateUserColor(authorId);
            }
        }

        // Handle reference mark
        const refMark = marks.find(m => m.type === 'reference');
        if (refMark && referencesByMarkId instanceof Map && getReferenceNumber) {
            const markId = refMark.attrs?.uid;

            // Get all references for this mark
            const refs = referencesByMarkId.get(markId) || [];

            // Get citation numbers for all references under this mark
            const refNumbers = refs
                .map(ref => getReferenceNumber(ref.localId))
                .filter(num => num !== null && num !== undefined)
                .join(',');

            if (refNumbers) {
                wrapped = (
                    <span key={`ref-${nodeKey}`} data-uid={markId} className="inline-ref">
                        {wrapped}
                        <sup style={{marginLeft: 4}}>
                            <Link href={`#ref-list`} underline="none">[{refNumbers}]</Link>
                        </sup>
                    </span>
                );
            }
        }

        // Handle wikidataEntity mark
        const wikidataEntityMark = marks.find(m => m.type === 'wikidataEntity');
        if (wikidataEntityMark) {
            const attrs = wikidataEntityMark.attrs || {};
            const id = attrs.id;
            const label = attrs.label;
            const url = attrs.url || null;
            const description = attrs.description || null;
            const entityType = attrs.entityType || null;
            const confidence = attrs.confidence ?? null;
            const nerScore = attrs.nerScore ?? confidence;
            const linkingScore = attrs.linkingScore ?? null;
            const imageUrl = resolveEntityAvatarUrl({
                wikidataId: id,
                imageUrl: attrs.imageUrl || null,
            });
            const isMatch = !!(currentEntityWikidataId && id && id === currentEntityWikidataId);

            wrapped = (
                <WikidataEntityMarkWrapper
                    key={`wikidata-${nodeKey}`}
                    id={id}
                    uid={attrs.uid || null}
                    label={label}
                    url={url}
                    description={description}
                    imageUrl={imageUrl}
                    entityType={entityType}
                    confidence={confidence}
                    nerScore={nerScore}
                    linkingScore={linkingScore}
                    isMatch={isMatch}
                    onWikidataEntityClick={onWikidataEntityClick}
                >
                    {wrapped}
                </WikidataEntityMarkWrapper>
            );
        }

        // Handle userMention mark (mark-based mentions)
        const userMentionMark = marks.find(m => m.type === 'userMention');
        if (userMentionMark) {
            const attrs = userMentionMark.attrs || {};
            const {id, label, name, email, position, headshot} = attrs;
            wrapped = (
                <span
                    key={`mention-${nodeKey}`}
                    style={getMentionPillStyle(false)}
                    onMouseEnter={(e) => {
                        Object.assign(e.currentTarget.style, getMentionPillStyle(true));
                    }}
                    onMouseLeave={(e) => {
                        Object.assign(e.currentTarget.style, getMentionPillStyle(false));
                    }}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onUserMentionClick?.({
                            id,
                            label,
                            name,
                            email,
                            position,
                            headshot,
                            anchorEl: event.currentTarget,
                        });
                    }}
                    data-mention-id={id}
                    data-mention-label={label}
                    data-mention-name={name}
                    title={name ? `${name}${email ? ` (${email})` : ''}${position ? ` - ${position}` : ''}` : label}
                >
                    {wrapped}
                </span>
            );
        }

        // Handle hashtag mark (mark-based hashtags)
        const hashtagMark = marks.find(m => m.type === 'hashtag');
        if (hashtagMark) {
            const attrs = hashtagMark.attrs || {};
            const {id, label, docId} = attrs;
            wrapped = (
                <span
                    key={`hashtag-${nodeKey}`}
                    style={getTagPillStyle(false)}
                    onMouseEnter={(e) => {
                        Object.assign(e.currentTarget.style, getTagPillStyle(true));
                    }}
                    onMouseLeave={(e) => {
                        Object.assign(e.currentTarget.style, getTagPillStyle(false));
                    }}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onHashtagClick?.({
                            id,
                            label,
                            docId,
                            anchorEl: event.currentTarget,
                        });
                    }}
                    data-tag-id={id}
                    data-tag-label={label}
                    data-tag-doc-id={docId}
                    title={`Tag: ${label || id}`}
                >
                    {wrapped}
                </span>
            );
        }

        // If there's a link mark, wrap the whole thing in an anchor using the mark attrs.
        const linkMark = marks.find(m => m.type === 'link');
        if (linkMark) {
            const attrs = linkMark.attrs || {};
            const href = attrs.href || undefined;
            const target = attrs.target || undefined;
            // Ensure safe rel when opening in a new tab
            const relAttr = attrs.rel || (target === '_blank' ? 'noopener noreferrer' : undefined);

            wrapped = (
                <Link
                    key={`link-${nodeKey}`}
                    href={href}
                    target={target}
                    rel={relAttr}
                    underline="hover"
                    sx={{color: 'primary.main'}}
                >
                    {wrapped}
                </Link>
            );
        }

        // Apply author highlighting if needed
        if (shouldHighlight && authorColor) {
            wrapped = (
                <span
                    key={`author-${nodeKey}`}
                    style={{
                        backgroundColor: `${authorColor}33`,
                        borderBottom: `2px solid ${authorColor}`,
                        padding: '2px 0',
                        transition: 'background-color 0.2s ease'
                    }}
                >
                    {wrapped}
                </span>
            );
        }

        return <React.Fragment key={nodeKey}>{wrapped}</React.Fragment>;
    }

    // nested nodes
    if (node.content && Array.isArray(node.content)) {
        return (
            <React.Fragment key={nodeKey}>
                {node.content.map((c, i) => (
                    <InlineRenderer key={`${nodeKey}-${i}`} node={c} nodeKey={`${nodeKey}-${i}`}
                                    referencesByMarkId={referencesByMarkId}
                                    referenceNumbers={referenceNumbers} getReferenceNumber={getReferenceNumber}
                                    selectedAuthorId={selectedAuthorId}
                                    onUserMentionClick={onUserMentionClick}
                                    onHashtagClick={onHashtagClick}
                                    onWikidataEntityClick={onWikidataEntityClick}
                                    currentEntityWikidataId={currentEntityWikidataId}/>
                ))}
            </React.Fragment>
        );
    }

    return null;
};

export default InlineRenderer;
